import React from 'react';
import { DeleteIconButton, IconButton } from '../../../icons';
import { IconType } from '../../../icons/Icon';
import { usePdfTemplateContext } from '../PdfTemplateEditorContext';
import PdfTemplateTextComponentProperties from './components/PdfTemplateTextComponentProperties';
import PdfTemplateTableComponentProperties from './components/PdfTemplateTableComponentProperties';
import PdfTemplateImageComponentProperties from './components/PdfTemplateImageComponentProperties';
import { PdfComponentType } from '../PdfComponent';
import PdfTemplateFieldComponentProperties from './components/PdfTemplateFieldComponentProperties';
import PdfTemplateBarcodeComponentProperties from './components/PdfTemplateBarcodeComponentProperties';
import { v4 } from 'uuid';

export default function PdfTemplateEditorPropertiesBar<T>() {
  const { components, setComponents, deleteComponent, editorSettings, updateEditorSettings, selected } =
    usePdfTemplateContext<T>();

  if (!selected) return null;

  return (
    <div
      className='relative flex h-full flex-col gap-4 overflow-y-auto overflow-x-hidden p-4'
      style={{ width: editorSettings.propertiesBarWidth }}
    >
      {/* Resizer handle */}
      <div
        className='absolute left-0 top-0 h-full w-2 cursor-col-resize overflow-x-hidden'
        onMouseDown={e => {
          const startX = e.clientX;
          const startWidth = editorSettings.propertiesBarWidth;
          const onMove = (ev: MouseEvent) => {
            const dx = ev.clientX - startX;
            const next = Math.max(330, Math.min(560, startWidth - dx));
            updateEditorSettings({ propertiesBarWidth: next });
          };
          const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
          };
          window.addEventListener('mousemove', onMove);
          window.addEventListener('mouseup', onUp);
        }}
      />

      {selected.type === PdfComponentType.staticText && <PdfTemplateTextComponentProperties component={selected} />}
      {selected.type === PdfComponentType.table && <PdfTemplateTableComponentProperties component={selected} />}
      {selected.type === PdfComponentType.image && <PdfTemplateImageComponentProperties component={selected} />}
      {selected.type === PdfComponentType.field && <PdfTemplateFieldComponentProperties component={selected} />}
      {selected.type === PdfComponentType.barcode && <PdfTemplateBarcodeComponentProperties component={selected} />}

      {/* Move to front/back actions */}
      {selected && (
        <div className='flex gap-2'>
          <IconButton
            type={IconType.arrowUp}
            text={'Move to front'}
            onClick={() => {
              setComponents([selected, ...components.filter(c => c.id !== selected.id)]);
            }}
          />
          <IconButton
            type={IconType.arrowDown}
            text={'Move to back'}
            onClick={() => {
              setComponents([...components.filter(c => c.id !== selected.id), selected]);
            }}
          />
          <IconButton
            type={IconType.container}
            text={'Copy'}
            onClick={() => {
              const columns = selected.type === PdfComponentType.table ? [...selected.columns] : undefined;
              if (columns) columns.forEach(col => (col.id = v4()));

              setComponents([
                { ...selected, id: v4(), x: selected.x + 10, y: selected.y + 10, columns: columns } as any,
                ...components,
              ]);
            }}
          />
          <DeleteIconButton onClick={event => deleteComponent(selected.id)} />
        </div>
      )}

      <div className='min-h-20'></div>
    </div>
  );
}
